/***
*
*   BUNDLE PACKAGING
*   Manage plans, features, add-ons, and settings
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Button, Spin, Modal, message, Typography, Space
} from 'antd';
import {
  PlusOutlined, ReloadOutlined
} from '@ant-design/icons';
import { Animate, usePermissions } from 'components/lib';
import {
  fetchBundlePackaging,
  fetchFeatures,
  createPlan,
  updatePlan,
  deletePlan,
  createFeature,
  updateFeature,
  deleteFeature,
  createAddOn,
  updateAddOn,
  deleteAddOn,
  invalidateBundlePackagingCache,
} from '../store/slices/bundlePackagingSlice';
import { PlanCard } from 'components/bundlePackaging/PlanCard';
import { PlanEditor } from 'components/bundlePackaging/PlanEditor';
import { AddOnsPanel } from 'components/bundlePackaging/AddOnsPanel';
import { FeaturesPanel } from 'components/bundlePackaging/FeaturesPanel';
import { CouponsPanel } from 'components/bundlePackaging/CouponsPanel';
import { PromotionCodesPanel } from 'components/bundlePackaging/PromotionCodesPanel';
import Style from 'components/bundlePackaging/bundlePackaging.tailwind.js';

const { Title, Text } = Typography;
const { confirm } = Modal;

export function BundlePackaging() {
  const dispatch = useDispatch();
  // const context = useContext(ViewContext);
  const { can } = usePermissions();

  // State
  const [activeTab, setActiveTab] = useState('plans');
  const [editingPlan, setEditingPlan] = useState(null);
  const [isNewPlan, setIsNewPlan] = useState(false);

  // Redux state
  const bundlePackagingState = useSelector((state) => state.bundlePackaging);
  const { data, features, loading, featuresLoading, mutationLoading, error } = bundlePackagingState;

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchBundlePackaging());
    dispatch(fetchFeatures());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(invalidateBundlePackagingCache());
    dispatch(fetchBundlePackaging());
    dispatch(fetchFeatures());
  };

  if (loading || featuresLoading) return <Spin />;

  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-500">Error loading bundle packaging data</p>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            style={{ marginTop: 16 }}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const {
    plans = [],
    add_ons,
    coupons,
    promotion_codes
  } = data || {};

  // Plan Handlers
  const handleCreatePlan = () => {
    setEditingPlan(null);
    setIsNewPlan(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setIsNewPlan(false);
  };

  const handleDuplicatePlan = (plan) => {
    setEditingPlan({
      ...plan,
      id: '',
      name: `${plan.name} (Copy)`,
    });
    setIsNewPlan(true);
  };

  const handleDeletePlan = (plan) => {
    confirm({
      title: 'Delete Plan',
      content: `Are you sure you want to delete "${plan.name}"? This will also remove it from Stripe.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deletePlan(plan.id)).unwrap();
          message.success('Plan deleted successfully');
          dispatch(fetchBundlePackaging());
        } catch (err) {
          message.error(err || 'Failed to delete plan');
        }
      },
    });
  };

  const handleSavePlan = async (planData) => {
    try {
      if (isNewPlan) {
        await dispatch(createPlan(planData)).unwrap();
        message.success('Plan created successfully');
      } else {
        await dispatch(updatePlan({ planId: planData.id, planData })).unwrap();
        message.success('Plan updated successfully');
      }
      setEditingPlan(null);
      setIsNewPlan(false);
      dispatch(fetchBundlePackaging());
    } catch (err) {
      message.error(err || 'Failed to save plan');
    }
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setIsNewPlan(false);
  };

  // Feature Handlers
  const handleSaveFeature = async (featureData, isNew) => {
    try {
      if (isNew) {
        await dispatch(createFeature(featureData)).unwrap();
        message.success('Feature created successfully');
      } else {
        await dispatch(updateFeature({ featureKey: featureData.key, featureData })).unwrap();
        message.success('Feature updated successfully');
      }
      dispatch(fetchFeatures());
    } catch (err) {
      message.error(err || 'Failed to save feature');
    }
  };

  const handleDeleteFeature = (featureKey) => {
    confirm({
      title: 'Delete Feature',
      content: 'Are you sure you want to delete this feature? Plans using this feature will need to be updated.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteFeature(featureKey)).unwrap();
          message.success('Feature deleted successfully');
          dispatch(fetchFeatures());
        } catch (err) {
          message.error(err || 'Failed to delete feature');
        }
      },
    });
  };

  // Add-on Handlers
  const handleCreateAddOn = async (addOnData) => {
    try {
      await dispatch(createAddOn(addOnData)).unwrap();
      message.success('Add-on created successfully');
      dispatch(fetchBundlePackaging());
    } catch (err) {
      message.error(err || 'Failed to create add-on');
    }
  };

  const handleUpdateAddOn = async (addOnId, addOnData) => {
    try {
      await dispatch(updateAddOn({ addOnId, addOnData })).unwrap();
      message.success('Add-on updated successfully');
      dispatch(fetchBundlePackaging());
    } catch (err) {
      message.error(err || 'Failed to update add-on');
    }
  };

  const handleDeleteAddOn = (addOnId) => {
    confirm({
      title: 'Delete Add-on',
      content: 'Are you sure you want to delete this add-on? Plans using this add-on will need to be updated.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteAddOn(addOnId)).unwrap();
          message.success('Add-on deleted successfully');
          dispatch(fetchBundlePackaging());
        } catch (err) {
          message.error(err || 'Failed to delete add-on');
        }
      },
    });
  };

  // Coupon Handlers (managed in Stripe)
  const handleSaveCoupon = async () => {
    message.info('Coupons must be managed in Stripe');
  };

  const handleDeleteCoupon = () => {
    message.info('Coupons must be managed in Stripe');
  };

  // Promo Code Handlers (managed in Stripe)
  const handleSavePromoCode = async () => {
    message.info('Promo codes must be managed in Stripe');
  };

  const handleDeletePromoCode = () => {
    message.info('Promo codes must be managed in Stripe');
  };

  const tabs = [
    { id: 'plans', label: 'Plans', count: plans.length },
    { id: 'features', label: 'Features', count: features?.length || 0 },
    { id: 'addons', label: 'Add-ons', count: Object.keys(add_ons || {}).length },
    { id: 'coupons', label: 'Coupons', count: Object.keys(coupons || {}).length },
    { id: 'promo-codes', label: 'Promo Codes', count: Object.keys(promotion_codes || {}).length },
  ];

  return (
    <Animate type="pop">
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Bundle Packaging</Title>
          <Text type="secondary">Manage plans, features, and pricing configuration</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
          {activeTab === 'plans' && can('bundle_packaging', 'edit') && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreatePlan}
              disabled={true}
            >
              Create Plan
            </Button>
          )}
        </Space>
      </div>

      {/* Tabs */}
      <div className={Style.tabsContainer}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? Style.tabActive : Style.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={Style.tabCount}>
                {tab.count}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'features' && (
        <FeaturesPanel
          features={features}
          onSave={handleSaveFeature}
          onDelete={handleDeleteFeature}
          loading={mutationLoading}
        />
      )}

      {activeTab === 'plans' && (
        <div className={Style.plansGrid}>
          {plans.length > 0 ? (
            plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onEdit={handleEditPlan}
                onDelete={handleDeletePlan}
                onDuplicate={handleDuplicatePlan}
              />
            ))
          ) : (
            <Card>
              <div className={Style.emptyState}>
                <p className={Style.emptyStateText}>No plans configured yet</p>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreatePlan}
                >
                  Create Your First Plan
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'addons' && (
        <AddOnsPanel
          addOns={add_ons}
          availableFeatures={features}
          onCreate={handleCreateAddOn}
          onUpdate={handleUpdateAddOn}
          onDelete={handleDeleteAddOn}
          loading={mutationLoading}
        />
      )}

      {activeTab === 'coupons' && (
        <CouponsPanel
          coupons={coupons}
          onSave={handleSaveCoupon}
          onDelete={handleDeleteCoupon}
          loading={false}
        />
      )}

      {activeTab === 'promo-codes' && (
        <PromotionCodesPanel
          promotionCodes={promotion_codes}
          coupons={coupons}
          onSave={handleSavePromoCode}
          onDelete={handleDeletePromoCode}
          loading={false}
        />
      )}

      {/* Plan Editor Modal */}
      {(editingPlan || isNewPlan) && (
        <PlanEditor
          plan={editingPlan}
          isNew={isNewPlan}
          availableFeatures={features}
          availableAddOns={add_ons}
          onSave={handleSavePlan}
          onCancel={handleCancelEdit}
          loading={mutationLoading}
        />
      )}
    </Animate>
  );
}